import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MoreVertical } from 'lucide-react';

interface SubscriptionDropdownProps {
  subscriptionId: string;
  onViewUsage?: (id: string) => void;
  onViewBilling?: (id: string) => void;
  onCancel?: (id: string) => void;
  isCancelling?: boolean;
}

export const SubscriptionDropdown: React.FC<SubscriptionDropdownProps> = ({
  subscriptionId,
  onViewUsage,
  onViewBilling,
  onCancel,
  isCancelling,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const menuItems = [
    {
      label: 'View usage details',
      onClick: () => onViewUsage?.(subscriptionId),
      className: 'text-gray-700 hover:bg-gray-50',
    },
    {
      label: 'View billing history',
      onClick: () => onViewBilling?.(subscriptionId),
      className: 'text-gray-700 hover:bg-gray-50',
    },
    {
      label: isCancelling ? 'Cancelling...' : 'Cancel',
      onClick: () => onCancel?.(subscriptionId),
      className: 'text-red-600 hover:bg-red-50',
      disabled: isCancelling,
    },
  ];

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 h-8 w-8"
        aria-label="More options"
      >
        <MoreVertical className="w-4 h-4 text-gray-600" />
      </Button>

      {isOpen && (
        <Card
          ref={dropdownRef}
          className="absolute right-0 top-full mt-1 w-56 bg-white shadow-lg border border-gray-200 rounded-lg z-50"
        >
          <div className="py-1">
            {menuItems.map((item, index) => (
              <div key={item.label}>
                <button
                  onClick={() => handleMenuItemClick(item.onClick)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${item.className}`}
                  disabled={item.disabled}
                >
                  {item.label}
                </button>
                {index < menuItems.length - 1 && (
                  <div className="h-px bg-gray-200 mx-4" />
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}; 
 